<div class="max-w-3xl mx-auto p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
        <div>
            <h1 class="text-2xl font-bold text-slate-900">
                {{ mode() === 'link' ? 'Link Organization to Space' : 'Add Organization' }}
            </h1>
            <p class="text-sm text-slate-500 mt-1">
                Register or link a company to your parking space and optionally set up a pool parking configuration.
            </p>
        </div>
        <button type="button" (click)="onCancel()"
            class="inline-flex items-center px-3.5 py-2 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 transition-colors">
            <svg class="h-4 w-4 mr-1.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Cancel
        </button>
    </div>

    <!-- Success Message -->
    @if (successMessage) {
    <div class="mb-6">
        <jjames-alert type="success" [dismissible]="true" (dismissed)="onClearSuccess()">
            {{ successMessage }}
        </jjames-alert>
    </div>
    }

    <!-- Error Message -->
    @if (errorMessage) {
    <div class="mb-6">
        <jjames-alert type="error" [dismissible]="true" (dismissed)="onClearError()">
            {{ errorMessage }}
        </jjames-alert>
    </div>
    }

    <jjames-card>
        <div class="p-6 space-y-6">

            <!-- 1. TARGET PARKING SPACE -->
            <div>
                <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    Target Parking Space <span class="text-red-500">*</span>
                </label>
                <div class="relative">
                    <select [value]="selectedSpaceId()" (change)="onSpaceSelected($any($event.target).value)"
                        class="w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition">
                        <option value="" disabled>— Select your parking space —</option>
                        @for (space of accessibleSpaces(); track space.id) {
                        <option [value]="space.id">{{ space.name }}</option>
                        }
                    </select>
                </div>
                <p class="text-xs text-slate-400 mt-1">
                    The organization and its pool rules will be scoped to this site.
                </p>
            </div>

            <!-- LINK EXISTING BANNER (When an existing org is picked) -->
            @if (mode() === 'link' && selectedOrgToLink()) {
            <div class="rounded-xl border border-blue-200 bg-blue-50/60 p-4 flex items-start justify-between gap-4">
                <div class="flex items-start gap-3">
                    <div class="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-base shrink-0">
                        {{ selectedOrgToLink()!.name?.charAt(0)?.toUpperCase() }}
                    </div>
                    <div>
                        <div class="flex items-center gap-2">
                            <h3 class="text-sm font-bold text-slate-900">{{ selectedOrgToLink()!.name }}</h3>
                            <span class="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full font-medium">Existing Platform Org</span>
                        </div>
                        <p class="text-xs text-slate-600 mt-0.5">
                            {{ selectedOrgToLink()!.description || 'This organization is already registered on the platform.' }}
                        </p>
                        <p class="text-xs text-blue-800 font-medium mt-1">
                            Linking will grant this organization access to your parking space without creating a duplicate record.
                        </p>
                    </div>
                </div>
                <button type="button" (click)="switchToCreateMode()"
                    class="text-xs text-slate-500 hover:text-slate-800 underline shrink-0">
                    Create new instead
                </button>
            </div>
            }

            <!-- 2. CREATE NEW ORG FORM (Shown when not in link mode) -->
            @if (mode() === 'create') {
            <form [formGroup]="orgForm" class="space-y-5">
                <div class="border-t border-slate-100 pt-4">
                    <h3 class="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-3">Organization Profile</h3>

                    <!-- Org Name with Autocomplete Search -->
                    <div class="relative">
                        <jjames-input inputId="name" label="Organization Name" type="text" formControlName="name"
                            [required]="true" [variant]="isFieldInvalid('name') ? 'error' : 'default'"
                            [errorMessage]="getFieldError('name')" placeholder="e.g. DHL Express, Siemens, JKU Logistics">
                        </jjames-input>

                        <!-- Live Autocomplete dropdown -->
                        @if (showLookupDropdown() && lookupResults().length > 0) {
                        <div class="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto p-1.5 space-y-1">
                            <div class="px-3 py-1 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                                Existing organizations matching name:
                            </div>
                            @for (match of lookupResults(); track match.id) {
                            <div (click)="selectExistingOrg(match)"
                                class="flex items-center justify-between p-2.5 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                                <div>
                                    <p class="text-sm font-medium text-slate-900">{{ match.name }}</p>
                                    @if (match.description) {
                                    <p class="text-xs text-slate-400 truncate max-w-md">{{ match.description }}</p>
                                    }
                                </div>
                                <div class="shrink-0 flex items-center gap-1.5">
                                    @if (match.isLinkedToSpace) {
                                    <span class="bg-amber-50 text-amber-700 text-xs px-2 py-0.5 rounded-full font-medium">Already in this space</span>
                                    } @else {
                                    <span class="bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded-md font-medium flex items-center gap-1">
                                        Link to this space &rarr;
                                    </span>
                                    }
                                </div>
                            </div>
                            }
                        </div>
                        }
                    </div>

                    <div class="mt-4">
                        <label for="description" class="block text-xs font-semibold text-slate-500 uppercase mb-1">Description</label>
                        <textarea id="description" formControlName="description" rows="2"
                            class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition"
                            placeholder="Optional notes or department details"></textarea>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        <div>
                            <jjames-input inputId="contactPerson" label="Contact Person" type="text"
                                formControlName="contactPerson" placeholder="Full name">
                            </jjames-input>
                        </div>
                        <div>
                            <jjames-input inputId="contactPhone" label="Contact Phone" type="text"
                                formControlName="contactPhone" placeholder="+43 ...">
                            </jjames-input>
                        </div>
                    </div>
                </div>

                <!-- Billing Address Section -->
                <div class="border-t border-slate-100 pt-4">
                    <h3 class="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-3">Billing Address</h3>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <jjames-input inputId="firstAddressLine" label="Address Line 1" type="text"
                                formControlName="firstAddressLine" placeholder="Street address">
                            </jjames-input>
                        </div>
                        <div>
                            <jjames-input inputId="secondAddressLine" label="Address Line 2" type="text"
                                formControlName="secondAddressLine" placeholder="Apt, suite, etc.">
                            </jjames-input>
                        </div>
                        <div>
                            <jjames-input inputId="locality" label="City" type="text" formControlName="locality"
                                placeholder="City">
                            </jjames-input>
                        </div>
                        <div>
                            <jjames-input inputId="postCode" label="Postal Code" type="text" formControlName="postCode"
                                placeholder="e.g. 4040">
                            </jjames-input>
                        </div>
                        <div>
                            <jjames-input inputId="region" label="Region / State" type="text" formControlName="region"
                                placeholder="e.g. Oberösterreich">
                            </jjames-input>
                        </div>
                        <div>
                            <label for="countryCode" class="block text-xs font-semibold text-slate-500 uppercase mb-1">Country</label>
                            <select id="countryCode" formControlName="countryCode"
                                class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition">
                                <option value="">Not specified</option>
                                @for (country of countries; track country.code) {
                                <option [value]="country.code">{{ country.name }} ({{ country.code }})</option>
                                }
                            </select>
                        </div>
                    </div>
                </div>
            </form>
            }

            <!-- 3. OPTIONAL INITIAL POOL CONFIGURATION -->
            <div class="border-t border-slate-100 pt-4">
                <div class="flex items-center justify-between">
                    <div>
                        <h3 class="text-sm font-semibold text-slate-900">Pool Parking Configuration</h3>
                        <p class="text-xs text-slate-500 mt-0.5">
                            Allocate shared parking capacity (quota) on this space for the organization's members.
                        </p>
                    </div>
                    <label class="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" [checked]="includePoolConfig()"
                            (change)="includePoolConfig.set(!includePoolConfig())" class="sr-only peer">
                        <div class="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                </div>

                @if (includePoolConfig()) {
                <form [formGroup]="poolForm" class="mt-4 p-4 rounded-xl border border-slate-200 bg-slate-50/70 space-y-4">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <jjames-input inputId="poolName" label="Pool Name" type="text" formControlName="name"
                                [required]="true" placeholder="e.g. DHL Standard Quota">
                            </jjames-input>
                        </div>
                        <div>
                            <jjames-input inputId="maxCarsAllowed" label="Max Cars Allowed (Capacity)" type="number"
                                formControlName="maxCarsAllowed" [required]="true" placeholder="5">
                            </jjames-input>
                        </div>
                    </div>

                    <!-- Allowed Zones -->
                    <div>
                        <label class="block text-xs font-semibold text-slate-500 uppercase mb-1.5">
                            Allowed Parking Zones on this Space
                        </label>
                        @if (loadingZones()) {
                        <p class="text-xs text-slate-400 italic">Loading parking zones…</p>
                        } @else if (spaceZones().length === 0) {
                        <p class="text-xs text-amber-600 italic">No parking zones defined for this space yet.</p>
                        } @else {
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                            @for (zone of spaceZones(); track zone.id) {
                            <label class="flex items-center gap-2.5 p-2.5 rounded-lg border border-slate-200 bg-white cursor-pointer hover:border-slate-300 transition">
                                <input type="checkbox" [checked]="selectedZoneIds().has(zone.id!)"
                                    (change)="toggleZoneSelection(zone.id!)"
                                    class="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                                <span class="text-sm font-medium text-slate-900">{{ zone.name }}</span>
                            </label>
                            }
                        </div>
                        }
                    </div>

                    <!-- Validity Dates -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label class="block text-xs font-semibold text-slate-500 uppercase mb-1">Valid From</label>
                            <input type="date" formControlName="validFrom"
                                class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition" />
                        </div>
                        <div>
                            <label class="block text-xs font-semibold text-slate-500 uppercase mb-1">Valid To (Optional)</label>
                            <input type="date" formControlName="validTo"
                                class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition" />
                        </div>
                    </div>

                    <!-- Overflow policy -->
                    <div>
                        <label class="block text-xs font-semibold text-slate-500 uppercase mb-1">Overflow Policy</label>
                        <select formControlName="overflowBehavior"
                            class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 transition">
                            @for (opt of overflowBehaviors; track opt.value) {
                            <option [value]="opt.value">{{ opt.label }} — {{ opt.hint }}</option>
                            }
                        </select>
                    </div>

                    <!-- Access Window Field -->
                    <app-access-window-field [(value)]="poolWindow"></app-access-window-field>
                </form>
                }
            </div>

            <!-- Form Actions -->
            <div class="flex items-center justify-end space-x-3 pt-4 border-t border-slate-100">
                <jjames-button type="button" variant="secondary" (clicked)="onCancel()" [disabled]="isSubmitting">
                    Cancel
                </jjames-button>
                <jjames-button type="button" variant="primary" (clicked)="onSubmit()"
                    [disabled]="isSubmitting || !selectedSpaceId() || (mode() === 'create' && orgForm.invalid)">
                    @if (isSubmitting) {
                        Processing...
                    } @else if (mode() === 'link') {
                        Link Organization to Space
                    } @else {
                        Create Organization
                    }
                </jjames-button>
            </div>

        </div>
    </jjames-card>
</div>